import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { useSupabaseQuery } from '../../hooks/useSupabase';
import { useAdminLayoutContext } from '../../components/admin/AdminLayout';
import {
  Plus,
  Edit2,
  Trash2,
  Building2,
  Users,
  X,
  AlertTriangle,
} from 'lucide-react';

interface Company {
  id: string;
  created_at: string;
  name: string;
  org_number?: string;
  contact_person?: string;
  phone?: string;
  email?: string;
  billing_email?: string;
  address?: string;
  postal_code?: string;
  city?: string;
  total_licenses?: number;
  settings?: {
    language?: string;
    theme?: string;
  };
  is_active?: boolean;
}

interface CompanyStats {
  totalLicenses: number;
  usedLicenses: number;
  availableLicenses: number;
}

export function CompanyManagement() {
  const { isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const { setHeaderAction } = useAdminLayoutContext();

  // Supabase query
  const { data: companies, loading: companiesLoading, refetch } = useSupabaseQuery<Company[]>(
    'companies',
    {
      select: '*',
      order: { column: 'name', ascending: true },
    }
  );

  const [showModal, setShowModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState<Company | null>(null);

  // Register header action for "Nytt firma" button
  useEffect(() => {
    setHeaderAction(
      <button
        onClick={() => {
          setShowModal(true);
        }}
        className="flex items-center gap-2 px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
      >
        <Plus className="w-5 h-5" />
        Nytt firma
      </button>
    );

    return () => setHeaderAction(null);
  }, [setHeaderAction]);

  const [formData, setFormData] = useState({
    name: '',
    orgNumber: '',
    contactPerson: '',
    phone: '',
    email: '',
    billingEmail: '',
    address: '',
    postalCode: '',
    city: '',
    language: 'no', // Hardkodet til norsk - kun norsk språk støttes
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingCompany) {
        const { error } = await supabase
          .from('companies')
          .update({
            name: formData.name,
            org_number: formData.orgNumber || null,
            contact_person: formData.contactPerson || null,
            phone: formData.phone || null,
            billing_email: formData.email || formData.billingEmail || null,
            address: formData.address || null,
            postal_code: formData.postalCode || null,
            city: formData.city || null,
            settings: {
              language: formData.language,
            },
            is_active: true,
          })
          .eq('id', editingCompany.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('companies')
          .insert({
            name: formData.name,
            org_number: formData.orgNumber || null,
            contact_person: formData.contactPerson || null,
            phone: formData.phone || null,
            billing_email: formData.email || formData.billingEmail || null,
            address: formData.address || null,
            postal_code: formData.postalCode || null,
            city: formData.city || null,
            settings: {
              language: formData.language,
            },
          });

        if (error) throw error;
      }

      setShowModal(false);
      setEditingCompany(null);
      resetForm();
      refetch();
    } catch (error: any) {
      console.error('Error saving company:', error);
      const errorMessage = error?.message || error?.toString() || 'Ukjent feil';
      alert(`Feil ved lagring: ${errorMessage}`);
    }
  };

  const handleEdit = (company: Company) => {
    setEditingCompany(company);
    setFormData({
      name: company.name,
      orgNumber: company.org_number || '',
      contactPerson: company.contact_person || '',
      phone: company.phone || '',
      email: company.email || '',
      billingEmail: company.billing_email || '',
      address: company.address || '',
      postalCode: company.postal_code || '',
      city: company.city || '',
      language: 'no', // Alltid norsk - kun norsk språk støttes
    });
    setShowModal(true);
  };

  const handleDelete = (company: Company) => {
    setCompanyToDelete(company);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!companyToDelete) return;

    try {
      const { error } = await supabase
        .from('companies')
        .delete()
        .eq('id', companyToDelete.id);

      if (error) throw error;
      setShowDeleteModal(false);
      setCompanyToDelete(null);
      refetch();
    } catch (error: any) {
      console.error('Error deleting company:', error);
      alert(`Feil ved sletting: ${error.message}`);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      orgNumber: '',
      contactPerson: '',
      phone: '',
      email: '',
      billingEmail: '',
      address: '',
      postalCode: '',
      city: '',
      language: 'no', // Hardkodet til norsk - kun norsk språk støttes
    });
  };

  const handleCancel = () => {
    setShowModal(false);
    setEditingCompany(null);
    resetForm();
  };

  if (!isSuperAdmin) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-400">Du har ikke tilgang til denne siden.</p>
        </div>
      </div>
    );
  }

  const companyList = companies || [];

  return (
    <div className="p-6 space-y-6">

      {/* Companies List */}
      {companiesLoading ? (
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400 dark:text-gray-500">Laster firmaer...</p>
        </div>
      ) : companyList.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <Building2 className="w-12 h-12 text-gray-400 dark:text-gray-500 dark:text-gray-500 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400 dark:text-gray-500 dark:text-gray-300 mb-2">Ingen firmaer registrert</p>
          <p className="text-gray-500 dark:text-gray-400 dark:text-gray-500 text-sm">Klikk på "Nytt firma" for å legge til det første firmaet</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {companyList.map((company) => {
            const CompanyStatsComponent = () => {
              // Fetch stats: count profiles in departments belonging to this company,
              // and sum allocated_seats from departments
              const { data: statsData } = useSupabaseQuery<any[]>(
                'departments',
                {
                  select: 'id, allocated_seats, profiles(count)',
                  filter: (q: any) => q.eq('company_id', company.id),
                  enabled: !!company.id,
                }
              );

              const totalLicenses = (statsData || []).reduce((sum: number, d: any) => sum + (d.allocated_seats || 0), 0);
              const usedLicenses = (statsData || []).reduce((sum: number, d: any) => {
                const count = d.profiles?.[0]?.count || 0;
                return sum + count;
              }, 0);
              const availableLicenses = Math.max(0, totalLicenses - usedLicenses);

              const licensePercentage = totalLicenses > 0 ? (usedLicenses / totalLicenses) * 100 : 0;

              return (
                <>
                  {/* Navn - helt til venstre */}
                  <div className="flex-shrink-0 w-64">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                      Firmanavn
                    </label>
                    <div className="flex items-center gap-3">
                      <Building2 className="w-6 h-6 text-blue-600 flex-shrink-0" />
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{company.name}</h3>
                        {company.is_active === false && (
                          <span className="px-2 py-1 bg-red-100 text-red-700 dark:text-red-300 text-xs rounded-full mt-1 inline-block">
                            Inaktiv
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Kontaktperson - neste bolk */}
                  <div className="flex-1 px-6 min-w-[200px]">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-2">
                      Kontaktperson
                    </label>
                    {company.contact_person ? (
                      <div className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                        <span className="text-base font-medium text-gray-900 dark:text-white">{company.contact_person}</span>
                      </div>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500 text-base">Ikke satt</span>
                    )}
                  </div>

                  {/* Lisenser - neste bolk med progress bar */}
                  <div className="flex-1 px-6 min-w-[250px]">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-2">
                      Lisenser
                    </label>
                    {totalLicenses > 0 ? (
                      <div className="space-y-2">
                        {/* Progress bar */}
                        <div className="relative">
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-green-500 to-green-600 transition-all duration-500 ease-out rounded-full"
                              style={{ width: `${Math.min(licensePercentage, 100)}%` }}
                            />
                          </div>
                          {/* Percentage indicator on bar */}
                          {licensePercentage > 10 && (
                            <div
                              className="absolute top-0 left-0 h-3 flex items-center"
                              style={{ width: `${Math.min(licensePercentage, 100)}%` }}
                            >
                              <span className="text-[10px] font-bold text-white ml-2">
                                {Math.round(licensePercentage)}%
                              </span>
                            </div>
                          )}
                        </div>
                        {/* Numbers below */}
                        <div className="flex items-center gap-3 text-sm">
                          <div className="flex items-center gap-1">
                            <span className="font-bold text-gray-900 dark:text-white">{usedLicenses}</span>
                            <span className="text-gray-500">av</span>
                            <span className="font-bold text-gray-900 dark:text-white">{totalLicenses}</span>
                            <span className="text-gray-500">brukt</span>
                          </div>
                          {availableLicenses > 0 && (
                            <>
                              <span className="text-gray-300">•</span>
                              <span className="text-green-600 font-medium">{availableLicenses} ledig</span>
                            </>
                          )}
                        </div>
                      </div>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500 text-sm">Ingen lisenser satt</span>
                    )}
                  </div>
                </>
              );
            };

            return (
              <div
                key={company.id}
                onClick={() => navigate(`/admin/departments?company_id=${company.id}&company_name=${encodeURIComponent(company.name)}`)}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow cursor-pointer hover:border-blue-300 dark:hover:border-blue-600"
              >
                <div className="flex items-center">
                  <CompanyStatsComponent />

                  {/* Redigeringsknapper - til slutt */}
                  <div className="flex gap-2 ml-auto">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(company);
                      }}
                      className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                      title="Rediger"
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(company);
                      }}
                      className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                      title="Slett"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {showModal && createPortal(
        <div
          className="fixed top-0 left-0 w-screen h-screen bg-black/50 dark:bg-black/70 flex items-center justify-center z-[9999]"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleCancel();
            }
          }}
        >
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white dark:text-white">
                {editingCompany ? 'Rediger firma' : 'Nytt firma'}
              </h2>
              <button
                onClick={handleCancel}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:text-gray-400"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Company Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Firmanavn *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Organization Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Organisasjonsnummer
                </label>
                <input
                  type="text"
                  value={formData.orgNumber}
                  onChange={(e) => setFormData({ ...formData, orgNumber: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="999 999 999"
                />
              </div>

              {/* Contact Person */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kontaktperson *
                </label>
                <input
                  type="text"
                  value={formData.contactPerson}
                  onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Phone and Email Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Telefon
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="+47 123 45 678"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    E-post
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="kontakt@firma.no"
                  />
                </div>
              </div>

              {/* Billing Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Faktura e-post
                </label>
                <input
                  type="email"
                  value={formData.billingEmail}
                  onChange={(e) => setFormData({ ...formData, billingEmail: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="faktura@firma.no"
                />
              </div>

              {/* Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Adresse
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Gateadresse 1"
                />
              </div>

              {/* Postal Code and City Row */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Postnummer
                  </label>
                  <input
                    type="text"
                    value={formData.postalCode}
                    onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0123"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sted
                  </label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Oslo"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  Avbryt
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {editingCompany ? 'Oppdater' : 'Opprett'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && companyToDelete && createPortal(
        <div
          className="fixed top-0 left-0 w-screen h-screen bg-black/50 dark:bg-black/70 flex items-center justify-center z-[9999]"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowDeleteModal(false);
              setCompanyToDelete(null);
            }
          }}
        >
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex items-start gap-4 mb-4">
              <div className="flex-shrink-0">
                <AlertTriangle className="w-12 h-12 text-red-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  Slett firma
                </h2>
                <p className="text-gray-700 mb-2">
                  Er du sikker på at du vil slette firmaet <strong>{companyToDelete.name}</strong>?
                </p>
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mt-3">
                  <p className="text-sm text-red-800 dark:text-red-400 font-semibold mb-2">
                    ADVARSEL: Dette kan ikke angres!
                  </p>
                  <p className="text-sm text-red-700 dark:text-red-300">
                    Alle avdelinger, brukere og all informasjon knyttet til dette firmaet vil bli permanent slettet.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteModal(false);
                  setCompanyToDelete(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                Avbryt
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold"
              >
                Ja, slett firmaet
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
